import React from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import ProductCard from '@components/shared/ProductCardV2/ProductCard';
import { useTheme } from '@components/theme';
import classNames from 'classnames';
import NavigatorIconsVersion
  from '@components/shared/Swiper/NavigatorIconsVersion/NavigatorIconsVersion';
import SwiperCore, { Navigation } from 'swiper';
import breakpoints from '@constants/breakpoints';
import { IVendorVariantInternal } from '@models/VendorVariant/i-vendor-variant';
import { hashCode } from '@lib/string';
import styles from './RecommendedProducts.module.scss';

SwiperCore.use([Navigation]);

interface IRecommendedProductsProps {
  sectionTitle: string;
  sectionSubTitle?: string;
  analyticsListId: string;
  recommendedProducts: IVendorVariantInternal[];
}

const SLIDES_VISIBLE_DESKTOP = 4;

export default function RecommendedProducts({
  sectionTitle,
  sectionSubTitle,
  analyticsListId,
  recommendedProducts,
}: IRecommendedProductsProps) {
  const theme = useTheme();

  if (!recommendedProducts.length || recommendedProducts.length < 3) {
    return null;
  }

  const navigatorId = hashCode(sectionTitle);

  return (
    <section className={styles.Container}>
      <div className={styles.TitleContainer}>
        <h3 className={classNames(
          theme.styles.Title2,
          styles.Title,
          !sectionSubTitle && styles.NoSubtitle
        )}
        >
          {sectionTitle}
        </h3>

        {recommendedProducts.length > SLIDES_VISIBLE_DESKTOP && (
          <NavigatorIconsVersion
            className={styles.Navigator}
            nextClassName={`next-${navigatorId}`}
            prevClassName={`prev-${navigatorId}`}
          />
        )}
      </div>

      {
        sectionSubTitle && (
          <p className={classNames(theme.styles.Body, styles.Subtitle)}>
            {sectionSubTitle}
          </p>
        )
      }

      <Swiper
        centeredSlides={false}
        slidesPerView={SLIDES_VISIBLE_DESKTOP}
        slidesPerGroup={1}
        spaceBetween={8}
        navigation={{
          nextEl: `.next-${navigatorId}`,
          prevEl: `.prev-${navigatorId}`,
          disabledClass: 'swiper-button-disabled'
        }}
        breakpoints={{
          [breakpoints['standard-phone']]: {
            slidesPerView: 1.2,
            centeredSlides: true
          },
          [breakpoints.desktop]: {
            slidesPerView: SLIDES_VISIBLE_DESKTOP,
            spaceBetween: 18,
          }
        }}
      >
        {
          recommendedProducts
            .filter(({ images }: IVendorVariantInternal) => images.length > 0)
            .map((vendorVariant) => {
              return (
                <SwiperSlide key={vendorVariant.id}>
                  <ProductCard
                    key={vendorVariant.id}
                    listPosition={0}
                    product={vendorVariant}
                    analytics={{
                      location: 'product',
                      listId: analyticsListId,
                    }}
                    showSaleBanner
                    imageAspectRatio={1}
                    showFullDetailsPage
                    isShopProductCard
                    showMaxDiscountText
                  />
                </SwiperSlide>
              );
            })
        }
      </Swiper>
    </section>
  );
}
